/**
 * **Geometria do carimbo visível — módulo PURO (sem DOM, sem rede, sem Node).**
 *
 * Conversão px↔fração e clamp nos limites do contrato (`zVisualPlacement` em `dtos.ts`),
 * com origem no canto **superior-esquerdo** da página (D-027) — o mesmo espaço de coordenadas
 * que o `format-engine` consome e que o DSS converte para pontos PDF.
 *
 * **Por que isto vive aqui e não no app.** O código nasceu em
 * `apps/signing-workbench/src/ui/lib/stamp-geometry.ts` e era a ÚNICA implementação da regra —
 * a bancada sabia clampar, a `/v1` não sabia nem que existia faixa de tamanho. Com o M5a o
 * signatário passa a arrastar o carimbo na cerimônia, então a mesma geometria precisa valer em
 * três superfícies (bancada, cerimônia e contrato). Copiar seria fabricar a terceira divergência
 * do mesmo conceito; a regra sobe para o contrato e os apps importam.
 *
 * **Browser-safe por construção** (GATE DI2-A): este módulo importa apenas de `./dtos`, que só
 * depende de `zod`. Nunca importar `node:*` aqui — o subpath `@argink/shared/stamp-geometry` é
 * consumido por `apps/web`, `apps/signer-ui` e `apps/signing-workbench`.
 *
 * O clamp é **conveniência de interação**, não autoridade: quem recusa um carimbo inválido é
 * `zVisualPlacement` na borda do servidor. Arrastar até o limite é UX; o servidor continua
 * verificando o que chega.
 */
import {
  STAMP_HEIGHT_MAX,
  STAMP_HEIGHT_MIN,
  STAMP_WIDTH_MAX,
  STAMP_WIDTH_MIN,
} from './dtos';

export { STAMP_WIDTH_MIN, STAMP_WIDTH_MAX, STAMP_HEIGHT_MIN, STAMP_HEIGHT_MAX };

export interface FractionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PageSize {
  width: number;
  height: number;
}

/** Tamanho inicial (dentro dos limites) usado nas posições rápidas e no carimbo padrão. */
export const DEFAULT_STAMP_WIDTH = 0.32;
export const DEFAULT_STAMP_HEIGHT = 0.08;

/** Margem das posições rápidas em relação à borda da página (fração). */
const QUICK_POSITION_MARGIN = 0.02;

export function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Aplica TODOS os limites do contrato, nesta ordem: width/height primeiro (independentes de
 * posição), x/y depois (usando o width/height já dentro da faixa, para que `x+width<=1` e
 * `y+height<=1` fiquem garantidos por construção, não por checagem separada).
 */
export function clampFractionRect(rect: FractionRect): FractionRect {
  const width = clampNumber(rect.width, STAMP_WIDTH_MIN, STAMP_WIDTH_MAX);
  const height = clampNumber(rect.height, STAMP_HEIGHT_MIN, STAMP_HEIGHT_MAX);
  const x = clampNumber(rect.x, 0, 1 - width);
  const y = clampNumber(rect.y, 0, 1 - height);
  return { x, y, width, height };
}

/**
 * Pixels do viewport → fração da página. É AQUI que zoom, scroll e tamanho de tela deixam de
 * importar: o que se persiste é a razão contra a página renderizada, não o pixel medido.
 */
export function pixelToFraction(px: PixelRect, page: PageSize): FractionRect {
  if (!(page.width > 0) || !(page.height > 0)) {
    throw new Error('pixelToFraction: dimensões de página inválidas');
  }
  return clampFractionRect({
    x: px.x / page.width,
    y: px.y / page.height,
    width: px.width / page.width,
    height: px.height / page.height,
  });
}

export function fractionToPixel(rect: FractionRect, page: PageSize): PixelRect {
  return {
    x: rect.x * page.width,
    y: rect.y * page.height,
    width: rect.width * page.width,
    height: rect.height * page.height,
  };
}

/** Move o retângulo por um delta em FRAÇÃO da página (já com clamp). */
export function moveFractionRect(rect: FractionRect, dx: number, dy: number): FractionRect {
  return clampFractionRect({ ...rect, x: rect.x + dx, y: rect.y + dy });
}

/** Redimensiona o retângulo por um delta em FRAÇÃO da página (já com clamp). Ponto de ancoragem
 *  é o canto superior-esquerdo do retângulo (x/y não mudam por causa do resize em si). */
export function resizeFractionRect(rect: FractionRect, dWidth: number, dHeight: number): FractionRect {
  return clampFractionRect({ ...rect, width: rect.width + dWidth, height: rect.height + dHeight });
}

export type QuickPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/** As 4 posições rápidas — `bottom-right` é o default (onde a assinatura manuscrita costuma ir). */
export function quickPositionRect(
  position: QuickPosition,
  width: number = DEFAULT_STAMP_WIDTH,
  height: number = DEFAULT_STAMP_HEIGHT,
): FractionRect {
  const w = clampNumber(width, STAMP_WIDTH_MIN, STAMP_WIDTH_MAX);
  const h = clampNumber(height, STAMP_HEIGHT_MIN, STAMP_HEIGHT_MAX);
  const left = QUICK_POSITION_MARGIN;
  const right = 1 - w - QUICK_POSITION_MARGIN;
  const top = QUICK_POSITION_MARGIN;
  const bottom = 1 - h - QUICK_POSITION_MARGIN;

  switch (position) {
    case 'bottom-right':
      return clampFractionRect({ x: right, y: bottom, width: w, height: h });
    case 'bottom-left':
      return clampFractionRect({ x: left, y: bottom, width: w, height: h });
    case 'top-right':
      return clampFractionRect({ x: right, y: top, width: w, height: h });
    case 'top-left':
      return clampFractionRect({ x: left, y: top, width: w, height: h });
    default: {
      const _exhaustive: never = position;
      throw new Error(`posição rápida desconhecida: ${String(_exhaustive)}`);
    }
  }
}

/** Carimbo padrão quando ninguém posicionou nada ainda (inferior direita). */
export function defaultStampRect(): FractionRect {
  return quickPositionRect('bottom-right');
}
